import axios from 'axios';
import { MAX_401_RETRY } from './Helpers/constants';

export const configureAxiosUserHash = async userHash => (axios.defaults.headers.common['x-Authorization-hash'] = userHash);
export const hasAxiosUserHash = () => !!axios.defaults.headers.common['x-Authorization-hash'];

// Local variables to prevent stacking of interceptors
let responseInterceptorId = null;
let requestInterceptorId = null;

export const configureAxios = () => {
  // Disable caching on every API request.
  axios.defaults.headers.common['Cache-Control'] = 'no-cache';
  // Bypass cache results on every API request.
  axios.defaults.headers.common['x-apicache-bypass'] = 'true';

  // Opt in to CORS credentials
  // This is required for cookies to be sent and received in cross-origin requests.
  axios.defaults.withCredentials = true;

  // Remove the previous interceptor (if exists)
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }

  responseInterceptorId = axios.interceptors.response.use(
    // pass through successful responses
    response => response,
    // handle errors
    async error => {
      if (!error.config) {
        // Short-circuit errors without a config (e.g. CORS failures, network errors)
        return Promise.reject(error?.response);
      }

      error.config._retryAuthAttempt = error.config._retryAuthAttempt || 0;

      if (error.response?.status === 401) {
        if (error.config._retryAuthAttempt < MAX_401_RETRY) {
          /*
            Retry the request after a 401 Unauthorized response
            SE-4387:
              - Fix issues with unexpected logout when revisiting stale ShopMy tabs
              - Browsers often reclaim memory by unloading stale tabs, and can take a few tries to re-populate cookies and site data
          */
          error.config._retryAuthAttempt++;
          await new Promise(resolve => setTimeout(resolve, 250)); // backoff
          return axios.request(error.config);
        }

        // cleanup
        if ('_retryAuthAttempt' in error.config) {
          delete error.config._retryAuthAttempt;
        }

        // check if we need to dispatch unauthorized event to log the user out and end the session
        if (error?.response?.data?.sessionActions?.forceSessionLogout) {
          window.dispatchEvent(new Event('shopmy:unauthorized'));
        }
      }

      return Promise.reject(error?.response);
    }
  );

  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
  }

  requestInterceptorId = axios.interceptors.request.use(config => {
    // read the CSRF Token cookie
    const match = document.cookie.match(/(?:^|; )shopmy_csrf_token=([^;]+)/);

    if (match) {
      config.headers['X-CSRF-Token'] = match[1];
    }

    return config;
  });
};

configureAxios();
